import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection, SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { Clock, Money } from '@factorialco/f0-react/icons/app'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  DistributorProductVariantsDocument,
  DistributorProductVariantsQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import RequestDeviceModal from 'modules/it_management/components/RequestDeviceModal'
import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'
import { useGetUpsellingData } from 'modules/upselling_kit/hooks/useGetUpsellingData'

import emptyStateImage from './Empty-state-device-catalog-card.png'

type DistributorProductVariant =
  DistributorProductVariantsQuery['itManagement']['distributorProductVariantsConnection']['nodes'][number]

const formatPrice = (
  cost: number | string | null | undefined,
  period: number | null | undefined
): string => {
  if (!cost) return '-'
  const costNumber = typeof cost === 'string' ? parseFloat(cost) : cost
  if (isNaN(costNumber)) return '-'
  if (period && period > 0) {
    return `From ${costNumber.toFixed(2)} €/month`
  }
  return `${costNumber.toFixed(2)} €`
}
const DeviceCatalogPage: React.FC = () => {
  const navigate = useNavigate()
  const { company, access } = useCurrentSession()
  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()

  const [selectedVariant, setSelectedVariant] = useState<DistributorProductVariant | null>(null)
  const [requestedVariants, setRequestedVariants] = useState<Set<string>>(new Set())
  const [_showBanner, setShowBanner] = useState(true)

  React.useEffect(() => {
    track(trackingEvents.itManagement.deviceCatalogEvents.goToDeviceCatalog, {
      coming_from: 'side_bar',
      company_id: company.id,
      user_id: access.id,
    })
  }, [company.id, access.id])

  const { upsellModalImage } = useGetUpsellingData({
    moduleRequested: AvailableModules.it_management,
  })

  const handleExpensesBannerClose = () => {
    setShowBanner(false)
  }

  const itemActions = useCallback(
    (item: DistributorProductVariant) => {
      const isRequested = requestedVariants.has(item.id)
      if (isRequested) {
        return [
          {
            label: i18n.t('it_management.device_catalog.actions.requested'),
            type: 'secondary' as const,
            icon: Clock,
            onClick: () => {}, // Disabled state - no action
          },
        ]
      }
      return [
        {
          label: i18n.t('it_management.device_catalog.actions.request'),
          type: 'secondary' as const,
          onClick: (event?: React.MouseEvent) => {
            event?.stopPropagation()
            track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceStart, {
              device_id: item.id,
              device_name: item.product?.name || item.sku || item.id,
              origin: 'catalog_card',
              company_id: company.id,
              user_id: access.id,
            })
            setSelectedVariant(item)
          },
        },
      ]
    },
    [requestedVariants, company.id, access.id]
  )

  const source = useGraphqlDataCollectionSource({
    document: DistributorProductVariantsDocument,
    fetchPolicy: 'network-only',
    perPage: 36,
    search: {
      enabled: true,
    },
    mapData: (data: DistributorProductVariantsQuery) => {
      return data?.itManagement.distributorProductVariantsConnection?.nodes || []
    },
    mapPagination: (data: DistributorProductVariantsQuery) => {
      return {
        total: data?.itManagement.distributorProductVariantsConnection?.totalCount || 0,
      }
    },
    mapVariables: ({ search }) => {
      return {
        search: search,
      }
    },
    itemActions,
    itemOnClick: (item: DistributorProductVariant) => {
      return () => {
        track(trackingEvents.itManagement.deviceCatalogEvents.goToDeviceDetail, {
          device_id: item.id,
          device_name: item.product?.name || item.sku || item.id,
          coming_from: 'catalog',
          company_id: company.id,
          user_id: access.id,
        })
        navigate(`/device-catalog/${item.id}`)
      }
    },
  })

  return (
    <ResponsivePage.Body
      authorized={isItManagementEnabled}
      error={featureError}
      loading={featureLoading}
      variant="full"
    >
      <Box paddingBottom="s32">
        <SectionHeader
          title={i18n.t('it_management.device_catalog.description.title')}
          description={i18n.t('it_management.device_catalog.description.subtitle')}
        />
      </Box>

      {!isItManagementEnabled && (
        <UpsellingKit.ProductBanner
          moduleRequested={AvailableModules.it_management}
          featureRequested={Features.it_management_v2_core}
          requestSource="device_catalog_page"
          requestReason={ValidReasons.MissingAddon}
          title={i18n.t('it_management.device_catalog.upsell_banner.title')}
          subtitle={i18n.t('it_management.device_catalog.upsell_banner.subtitle')}
          mediaUrl={upsellModalImage || ''}
          onClose={handleExpensesBannerClose}
          primaryAction={{
            label: i18n.t('project_management.upselling.cta'),
            variant: 'outline',
          }}
        />
      )}

      <OneDataCollection
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t('it_management.device_catalog.empty_state.title'),
            description: i18n.t('it_management.device_catalog.empty_state.description'),
          },
        }}
        visualizations={[
          {
            type: 'card',
            options: {
              title: (item: DistributorProductVariant) => {
                return item.product?.name ? item.product.name : item.sku || item.id
              },
              image: (item: DistributorProductVariant) => {
                return item.firstProductMediaImageUrl
                  ? buildUrl(item.firstProductMediaImageUrl)
                  : emptyStateImage
              },
              imageFit: 'contain',
              imageSize: 'lg',
              blurredBackground: false,
              cardProperties: [
                {
                  label: i18n.t('it_management.device_catalog.price_title'),
                  tooltip: i18n.t('it_management.device_catalog.price_title'),
                  icon: Money,
                  render: (item: DistributorProductVariant) => {
                    const price = formatPrice(item.firstOfferCost, item.firstOfferPeriod)
                    if (price === '-') return undefined
                    return {
                      type: 'text',
                      value: price,
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
      {selectedVariant && (
        <RequestDeviceModal
          variant={selectedVariant}
          onClose={() => setSelectedVariant(null)}
          onSuccess={() => {
            if (selectedVariant) {
              setRequestedVariants((prev) => new Set(prev).add(selectedVariant.id))
            }
          }}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default DeviceCatalogPage
